import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { communityService } from '../services/api';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
import ImageCropper from '../components/ImageCropper';
import { readFile } from '../utils/imageUtils';
import { isGenericEmailDomain } from '../utils/genericEmailDomains';

// ── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, type, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed dashboard-floating-bottom left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
      <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-bold max-w-md ${
        type === 'error' ? 'bg-error text-on-error' :
        type === 'warning' ? 'bg-secondary text-on-secondary' :
        'bg-primary text-on-primary'
      }`}>
        <span className="material-symbols-outlined text-base">
          {type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'check_circle'}
        </span>
        <span className="flex-1">{message}</span>
        <a onClick={onClose} className="ml-2 cursor-pointer opacity-70 hover:opacity-100 shrink-0">
          <span className="material-symbols-outlined text-base">close</span>
        </a>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

function CreateCommunityNew() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = getThemeVars(ACTIVE_THEME);

  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [success, setSuccess] = useState(false);
  const [newImage, setNewImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [toast, setToast] = useState({ message: '', type: 'success' });

  // Cropper state — al subir una imagen abrimos el modal para que el usuario
  // pueda ver la foto completa y recortar la parte que va a quedar como
  // foto cuadrada de la comunidad (se renderea en avatares/cards).
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperFile, setCropperFile] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain: '',
    allowDomainJoin: false,
    invitePolicy: 'ADMIN_ONLY',
  });

  const [errors, setErrors] = useState({});

  const steps = [t('Información básica'), t('Configuración de dominio'), t('Revisar y crear')];

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'success' }), 5000);
  };

  // ── Input handlers ─────────────────────────────────────────────────────

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;

    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    // Reset el input para que seleccionar el mismo archivo dos veces seguidas
    // dispare el onChange (sino el segundo file vuelve null).
    event.target.value = '';
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      showToast(t('Por favor selecciona una imagen valida'), 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast(t('La imagen no debe superar 5MB'), 'error');
      return;
    }

    try {
      const base64 = await readFile(file);
      setCropperImage(base64);
      setCropperFile(file);
      setCropperOpen(true);
    } catch {
      showToast(t('No se pudo procesar la imagen'), 'error');
    }
  };

  const handleCropSave = async (croppedFile) => {
    setNewImage(croppedFile);
    try {
      const base64 = await readFile(croppedFile);
      setImagePreview(base64);
    } catch {
      // Si falla el read, igual mantenemos el File (el upload usa newImage)
    }
    setCropperOpen(false);
    setCropperImage(null);
    setCropperFile(null);
  };

  const handleCropClose = () => {
    setCropperOpen(false);
    setCropperImage(null);
    setCropperFile(null);
  };

  const removeImage = () => {
    setNewImage(null);
    setImagePreview(null);
  };

  // ── Validation ─────────────────────────────────────────────────────────

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.name.trim()) {
          newErrors.name = t('El nombre de la comunidad es obligatorio');
        } else if (formData.name.length < 3) {
          newErrors.name = t('El nombre de la comunidad debe tener al menos 3 caracteres');
        } else if (formData.name.length > 30) {
          newErrors.name = t('El nombre de la comunidad debe tener menos de 30 caracteres');
        }

        if (!formData.description.trim()) {
          newErrors.description = t('La descripcion es obligatoria');
        } else if (formData.description.length > 200) {
          newErrors.description = t('La descripcion debe tener menos de 200 caracteres');
        }
        break;

      case 1:
        if (formData.allowDomainJoin) {
          if (!formData.domain) {
            newErrors.domain = t('El dominio es obligatorio cuando el autoingreso esta habilitado');
          } else if (!formData.domain.includes('.')) {
            newErrors.domain = t('Ingresa un dominio valido (ej: empresa.com)');
          } else if (isGenericEmailDomain(formData.domain)) {
            newErrors.domain = t('No se permite usar dominios de correo genericos o publicos (ej: gmail.com, yahoo.com, fibertel.com.ar). Usa el dominio de tu empresa o institucion.');
          }
        }
        break;

      case 2:
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) setActiveStep(prev => prev + 1);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  // ── Submit ─────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validateStep(2)) {
      return;
    }

    setSubmitting(true);
    setError('');
    setWarning('');

    try {
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        invitePolicy: formData.invitePolicy,
      };

      if (formData.allowDomainJoin && formData.domain) {
        submitData.domain = formData.domain.trim().toLowerCase();
        submitData.allowDomainJoin = true;
      }

      const response = await communityService.createCommunity(submitData);
      const newCommunity = response.data;

      if (newImage) {
        try {
          await communityService.uploadCommunityImage(newCommunity.id, newImage);
        } catch (uploadError) {
          setWarning(t('La comunidad fue creada, pero no se pudo subir el logo. Puedes agregarlo luego desde edicion.'));
        }
      }

      setSuccess(true);
      showToast(t('Comunidad creada con exito! Redirigiendo...'));

      setTimeout(() => {
        navigate(`/communities/${newCommunity.id}`);
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.message || t('Error al crear la comunidad. Intenta nuevamente.'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/communities" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-5xl mx-auto">

          {/* ── Card: everything inside ── */}
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant/20 overflow-hidden">

            {/* ── Header ── */}
            <div className="px-8 tablet-sm:px-10 pt-8 pb-0">
              <div className="flex items-center gap-3">
                <a
                  onClick={() => navigate(-1)}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-on-surface-variant cursor-pointer hover:bg-surface-container transition-colors"
                >
                  <span className="material-symbols-outlined text-xl">arrow_back</span>
                </a>
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-2xl text-primary">group</span>
                  <h1 className="text-2xl font-bold text-on-surface">{t('Crear nueva comunidad')}</h1>
                </div>
              </div>
            </div>

            {/* ── Stepper ── */}
            <div className="flex items-center gap-3 px-8 tablet-sm:px-10 pt-6 pb-8">
              {steps.map((label, i) => (
                <React.Fragment key={label}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${
                      i <= activeStep ? 'bg-primary text-on-primary' : 'border-2 border-outline-variant/40 text-on-surface-variant'
                    }`}>
                      {i < activeStep ? (
                        <span className="material-symbols-outlined text-sm">check</span>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className={`text-sm font-medium hidden tablet-sm:inline ${i === activeStep ? 'text-on-surface' : 'text-on-surface-variant/60'}`}>
                      {label}
                    </span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 rounded-full ${i < activeStep ? 'bg-primary' : 'bg-outline-variant/20'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="px-8 tablet-sm:hidden pb-2">
              <p className="text-sm font-semibold text-on-surface">{steps[activeStep]}</p>
            </div>

            {/* ── Form Content ── */}
            <div className="px-8 tablet-sm:px-10 pb-2" style={{ minHeight: '400px' }}>

              {/* ═══ Step 0: Basic Info ═══ */}
              {activeStep === 0 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-on-surface">{t('Información básica')}</h3>
                    <p className="text-sm text-on-surface-variant mt-1">{t('Completa los datos básicos de tu comunidad')}</p>
                  </div>

                  {/* Community Name -- floating label */}
                  <div className="relative mt-2">
                    <input
                      type="text"
                      id="cc-name"
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      maxLength={30}
                      className={`peer w-full border-2 rounded-md px-3.5 pt-5 pb-2 text-base text-on-surface placeholder-transparent transition-colors ${
                        errors.name ? 'border-error' : 'border-outline-variant/30 focus:border-primary'
                      }`}
                      style={{ backgroundColor: 'white' }}
                      placeholder={t('Nombre de la comunidad')}
                    />
                    <label
                      htmlFor="cc-name"
                      className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors"
                    >
                      {t('Nombre de la comunidad')} *
                    </label>
                    {errors.name ? (
                      <p className="text-xs text-error mt-1.5">{errors.name}</p>
                    ) : (
                      <p className="text-xs text-on-surface-variant/60 mt-1.5">{t('Elige un nombre único para tu comunidad')}</p>
                    )}
                  </div>

                  {/* Description -- floating label */}
                  <div className="relative">
                    <textarea
                      id="cc-description"
                      value={formData.description}
                      onChange={handleInputChange('description')}
                      maxLength={200}
                      rows={2}
                      className={`peer w-full border-2 rounded-md px-3.5 pt-5 pb-2 text-base text-on-surface placeholder-transparent transition-colors resize-none ${
                        errors.description ? 'border-error' : 'border-outline-variant/30 focus:border-primary'
                      }`}
                      style={{ backgroundColor: 'white' }}
                      placeholder={t('Descripción')}
                    />
                    <label
                      htmlFor="cc-description"
                      className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors"
                    >
                      {t('Descripción')} *
                    </label>
                    <div className="flex items-center justify-between mt-1.5">
                      {errors.description ? (
                        <p className="text-xs text-error">{errors.description}</p>
                      ) : (
                        <p className="text-xs text-on-surface-variant/60">{t('Cortito, máximo 200 caracteres. Tocá una sugerencia o escribí la tuya.')}</p>
                      )}
                      <span className="text-xs text-on-surface-variant/40">{formData.description.length}/200</span>
                    </div>
                    {/* Chips de sugerencia: rellenan la descripción al tap. */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {['Universidad', 'Colegio', 'Trabajo', 'Club', 'Deporte', 'Amigos', 'Cerámica', 'Vecinos'].map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => handleInputChange('description')({ target: { value: s } })}
                          className="px-2.5 py-1 rounded-full text-xs font-bold border border-outline-variant/40 text-on-surface-variant hover:bg-surface-container hover:border-primary/40 hover:text-primary transition-colors"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Foto de la comunidad */}
                  <div>
                    <label className="block text-base font-medium text-on-surface mb-3">
                      {t('Foto de la comunidad')}
                    </label>

                    {imagePreview ? (
                      <div className="rounded-xl border border-outline-variant/20 overflow-hidden max-w-xs">
                        <div className="aspect-square bg-surface-container flex items-center justify-center">
                          <img
                            src={imagePreview}
                            alt={t('Foto de la comunidad')}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 bg-white">
                          <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-outline-variant/30 text-sm font-medium text-on-surface-variant cursor-pointer hover:bg-surface-container transition-colors">
                            <span className="material-symbols-outlined text-lg">cloud_upload</span>
                            {t('Cambiar')}
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={handleImageChange}
                            />
                          </label>
                          <a
                            onClick={removeImage}
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-error cursor-pointer hover:bg-error-container transition-colors"
                          >
                            <span className="material-symbols-outlined text-xl">delete</span>
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-outline-variant/30 rounded-xl p-8 text-center bg-surface-container-lowest max-w-xs">
                        <span className="material-symbols-outlined text-4xl text-on-surface-variant/40 mb-3 block">cloud_upload</span>
                        <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-primary text-primary text-sm font-medium cursor-pointer hover:bg-primary/5 transition-colors">
                          <span className="material-symbols-outlined text-lg">cloud_upload</span>
                          {t('Subir foto')}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                        </label>
                        <p className="text-xs text-on-surface-variant/50 mt-3">
                          {t('Opcional. JPG, PNG o GIF. Máximo 5MB. Vas a poder recortar la imagen al cuadrado antes de subirla.')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ═══ Step 1: Domain Configuration ═══ */}
              {activeStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-on-surface">{t('Configuración de dominio')}</h3>
                    <p className="text-sm text-on-surface-variant mt-1">{t('Configura el ingreso automático según dominios de correo')}</p>
                  </div>

                  {/* Info alert */}
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary-container text-on-secondary-container">
                    <span className="material-symbols-outlined text-lg mt-0.5 shrink-0">info</span>
                    <p className="text-sm">
                      {t('Si habilitas el autoingreso por dominio, los usuarios con correos verificados del dominio indicado podrán unirse automáticamente a esta comunidad.')}
                    </p>
                  </div>

                  {/* Toggle switch */}
                  <div className="flex items-center gap-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={formData.allowDomainJoin}
                        onChange={handleInputChange('allowDomainJoin')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-outline-variant/40 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                    <span className="text-sm font-medium text-on-surface">
                      {t('Permitir autoingreso para usuarios con dominio de correo coincidente')}
                    </span>
                  </div>

                  {/* Domain input (conditional) */}
                  {formData.allowDomainJoin && (
                    <div className="space-y-4">
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant/60">
                          <span className="material-symbols-outlined text-xl">email</span>
                        </div>
                        <input
                          type="text"
                          id="cc-domain"
                          value={formData.domain}
                          onChange={handleInputChange('domain')}
                          placeholder={t('empresa.com')}
                          className={`peer w-full border-2 rounded-md pl-11 pr-3.5 pt-5 pb-2 text-base text-on-surface placeholder-transparent transition-colors ${
                            errors.domain ? 'border-error' : 'border-outline-variant/30 focus:border-primary'
                          }`}
                          style={{ backgroundColor: 'white' }}
                        />
                        <label
                          htmlFor="cc-domain"
                          className="absolute left-10 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors"
                        >
                          {t('Dominio de correo')} *
                        </label>
                        {errors.domain ? (
                          <p className="text-xs text-error mt-1.5">{errors.domain}</p>
                        ) : (
                          <p className="text-xs text-on-surface-variant/60 mt-1.5">{t('Ingresa el dominio (ej: empresa.com)')}</p>
                        )}
                      </div>

                      {/* Example box */}
                      <div className="flex items-start gap-3 p-4 rounded-xl bg-surface-container">
                        <span className="material-symbols-outlined text-primary text-lg mt-0.5 shrink-0">lightbulb</span>
                        <p className="text-sm text-on-surface-variant">
                          <strong>{t('Ejemplo:')}</strong>{' '}
                          {t('si ingresas "{{domain}}", usuarios con correos como "{{email1}}" o "{{email2}}" podrán unirse automáticamente luego de verificar su correo.', {
                            domain: 'empresa.com',
                            email1: 'juan@empresa.com',
                            email2: 'ana@empresa.com',
                          })}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Invite Policy */}
                  <div className="relative">
                    <label
                      htmlFor="cc-invite-policy"
                      className="block text-sm font-medium text-on-surface-variant mb-2"
                    >
                      {t('Política de invitaciones')} *
                    </label>
                    <select
                      id="cc-invite-policy"
                      value={formData.invitePolicy}
                      onChange={handleInputChange('invitePolicy')}
                      className="w-full border-2 border-outline-variant/30 rounded-md px-3.5 py-2 text-base text-on-surface focus:border-primary focus:outline-none transition-colors"
                      style={{ backgroundColor: 'white' }}
                    >
                      <option value="ADMIN_ONLY">
                        {t('Solo el creador puede invitar')}
                      </option>
                      <option value="OPEN">
                        {t('Miembros pueden invitar')}
                      </option>
                    </select>
                    <p className="text-xs text-on-surface-variant/60 mt-1.5">
                      {t('Define quién puede enviar invitaciones a tu comunidad')}
                    </p>
                  </div>
                </div>
              )}

              {/* ═══ Step 2: Review ═══ */}
              {activeStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-on-surface">{t('Revisa tu comunidad')}</h3>
                    <p className="text-sm text-on-surface-variant mt-1">{t('Revisa los detalles antes de crear tu comunidad')}</p>
                  </div>

                  {/* Review card */}
                  <div className="rounded-xl border border-outline-variant/20 bg-white overflow-hidden">
                    <div className="p-6 space-y-5">

                      {/* Name */}
                      <div>
                        <p className="text-xs font-medium text-on-surface-variant uppercase tracking-wider mb-1">
                          {t('Nombre de la comunidad')}
                        </p>
                        <p className="text-base text-on-surface font-medium">{formData.name}</p>
                      </div>

                      {/* Description */}
                      <div>
                        <p className="text-xs font-medium text-on-surface-variant uppercase tracking-wider mb-1">
                          {t('Descripción')}
                        </p>
                        <p className="text-sm text-on-surface leading-relaxed">{formData.description}</p>
                      </div>

                      {/* Foto de la comunidad preview */}
                      {imagePreview && (
                        <div>
                          <p className="text-xs font-medium text-on-surface-variant uppercase tracking-wider mb-2">
                            {t('Foto')}
                          </p>
                          <div className="w-40 h-40 rounded-lg overflow-hidden bg-surface-container">
                            <img
                              src={imagePreview}
                              alt={t('Foto de la comunidad')}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      )}

                      {/* Configuration */}
                      <div>
                        <p className="text-xs font-medium text-on-surface-variant uppercase tracking-wider mb-2">
                          {t('Configuración')}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {formData.allowDomainJoin ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary-container text-on-primary-container">
                              <span className="material-symbols-outlined text-sm">check_circle</span>
                              {t('Auto-join por dominio habilitado')}: @{formData.domain}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-secondary-container text-on-secondary-container">
                              <span className="material-symbols-outlined text-sm">lock</span>
                              {t('Sin auto-join por dominio')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Invite Policy */}
                      <div>
                        <p className="text-xs font-medium text-on-surface-variant uppercase tracking-wider mb-2">
                          {t('Política de invitaciones')}
                        </p>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                          formData.invitePolicy === 'ADMIN_ONLY' ? 'bg-primary-container text-on-primary-container' :
                          'bg-tertiary-container text-on-tertiary-container'
                        }`}>
                          <span className="material-symbols-outlined text-sm">
                            {formData.invitePolicy === 'ADMIN_ONLY' ? 'shield' : 'public'}
                          </span>
                          {formData.invitePolicy === 'ADMIN_ONLY' ? t('Solo el creador puede invitar') :
                           t('Miembros pueden invitar')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Alerts */}
                  {error && (
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-error-container text-on-error-container">
                      <span className="material-symbols-outlined text-lg mt-0.5 shrink-0">error</span>
                      <p className="text-sm">{error}</p>
                    </div>
                  )}

                  {warning && (
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary-container text-on-secondary-container">
                      <span className="material-symbols-outlined text-lg mt-0.5 shrink-0">warning</span>
                      <p className="text-sm">{warning}</p>
                    </div>
                  )}

                  {success && (
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-primary-container text-on-primary-container">
                      <span className="material-symbols-outlined text-lg mt-0.5 shrink-0">check_circle</span>
                      <p className="text-sm font-medium">{t('Comunidad creada con exito! Redirigiendo...')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Footer Actions ── */}
            <div className="flex flex-col-reverse mobile-sm:flex-row mobile-sm:items-center mobile-sm:justify-between gap-3 px-8 tablet-sm:px-10 py-5 mt-6 border-t border-outline-variant/15">
              <a
                onClick={activeStep > 0 ? handleBack : undefined}
                className={`w-full mobile-sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeStep > 0 ? 'text-on-surface-variant cursor-pointer hover:bg-surface-container' : 'text-on-surface-variant/30 pointer-events-none'
                }`}
              >
                <span className="material-symbols-outlined text-lg">arrow_back</span>
                {t('Atrás')}
              </a>

              <div className="flex flex-col mobile-sm:flex-row items-center gap-3 w-full mobile-sm:w-auto">
                <a
                  onClick={() => navigate(-1)}
                  className="w-full mobile-sm:w-auto px-5 py-2.5 rounded-lg text-center text-sm font-medium text-on-surface-variant cursor-pointer hover:bg-surface-container border border-outline-variant/30 transition-colors"
                >
                  {t('Cancelar')}
                </a>

                {activeStep === steps.length - 1 ? (
                  <a
                    onClick={!submitting && !success ? handleSubmit : undefined}
                    className={`w-full mobile-sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-primary text-on-primary rounded-lg text-sm font-semibold shadow-sm transition-all ${
                      submitting || success ? 'opacity-50 pointer-events-none' : 'cursor-pointer hover:opacity-90 active:scale-[0.98]'
                    }`}
                  >
                    {submitting ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <span className="material-symbols-outlined text-lg">check</span>
                    )}
                    {submitting ? t('Creando...') : t('Crear comunidad')}
                  </a>
                ) : (
                  <a
                    onClick={handleNext}
                    className="w-full mobile-sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-primary text-on-primary rounded-lg text-sm font-semibold cursor-pointer hover:opacity-90 active:scale-[0.98] transition-all"
                  >
                    {t('Siguiente')}
                    <span className="material-symbols-outlined text-lg">arrow_forward</span>
                  </a>
                )}
              </div>
            </div>
          </div>

      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'success' })} />

      <ImageCropper
        open={cropperOpen}
        image={cropperImage}
        originalFile={cropperFile}
        onSave={handleCropSave}
        onClose={handleCropClose}
      />
        </div>
      </main>
    </div>
  );
}

export default CreateCommunityNew;
